import { useState, useEffect } from 'react';
import { Project } from '../../../mocks/portfolio';

const CATEGORY_ICONS: Record<string, string> = {
  'Apartment Management': 'ri-building-2-line',
  'Residential Remodeling': 'ri-home-heart-line',
  'Commercial Remodeling': 'ri-store-2-line',
  'General Maintenance': 'ri-hammer-line',
};

const CATEGORY_COLORS: Record<string, string> = {
  'Apartment Management': 'bg-[#B8E2E8] text-[#05294A]',
  'Residential Remodeling': 'bg-[#7499CE]/20 text-[#7499CE]',
  'Commercial Remodeling': 'bg-[#7499CE]/20 text-[#7499CE]',
  'General Maintenance': 'bg-slate-100 text-slate-700',
};

interface Props {
  project: Project | null;
  onClose: () => void;
}

export default function ProjectModal({ project, onClose }: Props) {
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setActiveImg(0);
  }, [project]);

  useEffect(() => {
    if (project) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [project]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!project) return null;

  const allImages = [project.mainImage, ...project.gallery];
  const iconClass = CATEGORY_ICONS[project.category] ?? 'ri-briefcase-line';
  const colorClass = CATEGORY_COLORS[project.category] ?? 'bg-slate-100 text-slate-700';

  const prevImg = () => setActiveImg((p) => (p === 0 ? allImages.length - 1 : p - 1));
  const nextImg = () => setActiveImg((p) => (p === allImages.length - 1 ? 0 : p + 1));

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#05294A]/80 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl overflow-hidden w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-[#05294A]/70 text-white hover:bg-[#05294A] transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="flex flex-col md:flex-row overflow-y-auto">
          {/* Left: Image gallery */}
          <div className="md:w-[55%] bg-[#05294A] flex-shrink-0">
            {/* Main image */}
            <div className="relative h-72 md:h-80 overflow-hidden">
              <img
                src={allImages[activeImg]}
                alt={`${project.title} — image ${activeImg + 1}`}
                className="w-full h-full object-cover object-top transition-all duration-300"
              />
              {/* Nav arrows */}
              <button
                onClick={prevImg}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-[#05294A]/60 text-white hover:bg-[#67CBFF] hover:text-[#05294A] transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                onClick={nextImg}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-[#05294A]/60 text-white hover:bg-[#67CBFF] hover:text-[#05294A] transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
              {/* Image counter */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#05294A]/60 backdrop-blur-sm rounded-full text-white text-xs font-medium">
                {activeImg + 1} / {allImages.length}
              </div>
            </div>

            {/* Thumbnails */}
            <div className="flex gap-2 p-3 bg-[#032240]">
              {allImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImg(idx)}
                  className={`relative flex-1 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                    activeImg === idx ? 'border-[#67CBFF]' : 'border-transparent opacity-60 hover:opacity-90'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover object-top" />
                </button>
              ))}
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-4 divide-x divide-[#0A3D6A] bg-[#032240]">
              {project.stats.map((stat) => (
                <div key={stat.label} className="px-3 py-4 text-center">
                  <p className="text-white font-bold text-base">{stat.value}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Project details */}
          <div className="flex-1 p-7 overflow-y-auto">
            {/* Category badge */}
            <div className="flex items-center gap-2 mb-4">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${colorClass}`}>
                <i className={`${iconClass} text-xs`} />
                {project.category}
              </span>
              <span className="text-slate-400 text-xs">{project.location} · {project.year}</span>
            </div>

            <h2 className="text-slate-900 font-bold text-xl leading-snug mb-4">
              {project.title}
            </h2>

            <div className="flex items-center gap-1.5 text-slate-500 text-sm mb-5">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-line text-[#05294A] text-sm" />
              </div>
              <span>{project.location}</span>
            </div>

            <div className="w-10 h-0.5 bg-[#05294A] mb-5" />

            <p className="text-slate-600 text-sm leading-relaxed mb-6">
              {project.description}
            </p>

            {/* Scope of work */}
            <div>
              <p className="text-slate-900 font-bold text-sm mb-3 uppercase tracking-wide text-xs">
                Scope of Work
              </p>
              <div className="grid grid-cols-1 gap-2">
                {project.scope.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#B8E2E8] text-[#05294A] shrink-0">
                      <i className="ri-check-line text-xs" />
                    </div>
                    <span className="text-slate-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                onClose();
                setTimeout(() => {
                  const el = document.querySelector('#contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="mt-7 flex items-center justify-center gap-2 w-full py-3 bg-[#05294A] text-white rounded-lg text-sm font-semibold hover:bg-[#032240] transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>Get a Similar Quote</span>
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
